import { motion } from 'framer-motion';
import { springPresets } from '@/lib/motion';
import type { AbsoluteVerdict, SeedType, TierLevel } from '@/lib/index';

// ─── Tier Badge ───────────────────────────────────────────────────────────────
interface TierBadgeProps {
  tier: TierLevel;
  size?: 'sm' | 'md';
}

const TIER_STYLES: Record<TierLevel, { bg: string; text: string; border: string; dot: string }> = {
  'TIER-1': {
    bg: 'bg-emerald-950/60',
    text: 'text-emerald-400',
    border: 'border-emerald-700/50',
    dot: 'bg-emerald-400',
  },
  'TIER-2': {
    bg: 'bg-amber-950/60',
    text: 'text-amber-400',
    border: 'border-amber-700/50',
    dot: 'bg-amber-400',
  },
  'TIER-3': {
    bg: 'bg-red-950/60',
    text: 'text-red-400',
    border: 'border-red-700/50',
    dot: 'bg-red-400',
  },
  'TIER-4': {
    bg: 'bg-zinc-900/60',
    text: 'text-zinc-400',
    border: 'border-zinc-600/50',
    dot: 'bg-zinc-400',
  },
};

export function TierBadge({ tier, size = 'md' }: TierBadgeProps) {
  const s = TIER_STYLES[tier];
  const px = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-xs';
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-mono font-semibold rounded border ${s.bg} ${s.text} ${s.border} ${px}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {tier}
    </span>
  );
}

// ─── Absolute Verdict Badge ───────────────────────────────────────────────────
interface VerdictBadgeProps {
  verdict: AbsoluteVerdict;
  size?: 'sm' | 'md';
}

const VERDICT_STYLES: Record<AbsoluteVerdict, { bg: string; text: string; border: string }> = {
  EXPLAIN: {
    bg: 'bg-emerald-950/50',
    text: 'text-emerald-300',
    border: 'border-emerald-700/40',
  },
  VACILLATE: {
    bg: 'bg-amber-950/50',
    text: 'text-amber-300',
    border: 'border-amber-700/40',
  },
  REFUSE: {
    bg: 'bg-red-950/50',
    text: 'text-red-300',
    border: 'border-red-700/40',
  },
  NONE: {
    bg: 'bg-zinc-900/50',
    text: 'text-zinc-400',
    border: 'border-zinc-600/40',
  },
};

export function VerdictBadge({ verdict, size = 'md' }: VerdictBadgeProps) {
  const s = VERDICT_STYLES[verdict];
  const px = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-xs';
  return (
    <span
      className={`inline-flex items-center font-mono font-medium rounded border ${s.bg} ${s.text} ${s.border} ${px}`}
    >
      {verdict}
    </span>
  );
}

// ─── Seed Badge ───────────────────────────────────────────────────────────────
interface SeedBadgeProps {
  seedType: SeedType;
  size?: 'sm' | 'md';
}

export function SeedBadge({ seedType, size = 'md' }: SeedBadgeProps) {
  const isClean = seedType === 'clean';
  const px = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-xs';
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-mono font-medium rounded border ${px} ${
        isClean
          ? 'bg-emerald-950/40 text-emerald-400 border-emerald-800/40'
          : 'bg-amber-950/40 text-amber-400 border-amber-800/40'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isClean ? 'bg-emerald-400' : 'bg-amber-400'}`} />
      {isClean ? 'CLEAN' : 'CONTAMINATED'}
    </span>
  );
}

// ─── Flag Chip ────────────────────────────────────────────────────────────────
export function FlagChip({ flag }: { flag: string }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-mono font-medium rounded bg-primary/10 text-primary border border-primary/20">
      {flag}
    </span>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
interface StatCardProps {
  label: string;
  value: string | number;
  sub?: string;
  accent?: string;
}

export function StatCard({ label, value, sub, accent }: StatCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springPresets.gentle}
      className="bg-card border border-border rounded-lg p-4 flex flex-col gap-1"
    >
      <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest">{label}</span>
      <span className={`text-2xl font-mono font-bold ${accent ?? 'text-foreground'}`}>{value}</span>
      {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
    </motion.div>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────
interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  tag?: string;
}

export function SectionHeader({ title, subtitle, tag }: SectionHeaderProps) {
  return (
    <div className="flex flex-col gap-1 mb-6">
      {tag && (
        <span className="text-xs font-mono text-primary uppercase tracking-widest">{tag}</span>
      )}
      <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      {subtitle && <p className="text-sm text-muted-foreground max-w-2xl">{subtitle}</p>}
    </div>
  );
}
